/**
 * Across Protocol configuration for bridge-first Nado funding.
 *
 * Across uses intent-based bridging: relayers front tokens on Ink, then
 * rebalance in the background.
 *
 * Verified via Across API:
 *   GET https://app.across.to/api/available-routes?originChainId=42161&destinationChainId=57073
 *   → supports USDT/USDT0 and USDC routes into Ink.
 */

import { defineChain } from 'viem';

export const ACROSS_API_BASE_URL = 'https://app.across.to/api';
export const ACROSS_DEFAULT_INTEGRATOR_ID = '0xdead';
export const ACROSS_INK_CHAIN_ID = 57073;
export const ACROSS_NADO_PRODUCT_ID = 0;
export const ACROSS_USDC_NADO_PRODUCT_ID = 5;

export interface AcrossTokenConfig {
  chainId: number;
  symbol: 'USDC' | 'USDT' | 'USDT0';
  address: `0x${string}`;
  decimals: 6;
  bridgeType: 'across' | 'none';
  destinationToken?: `0x${string}`;
  productId?: 0 | 5;
}

export interface AcrossDisplayChain {
  chainId: number;
  name: string;
  nativeSymbol: string;
  blockExplorerUrl: string;
  isPrimary: boolean;
}

/** Chain definitions with reliable RPCs for Across SDK */
export const acrossEthereum = defineChain({
  id: 1,
  name: 'Ethereum',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://ethereum-rpc.publicnode.com', 'https://rpc.ankr.com/eth'] },
  },
  blockExplorers: {
    default: { name: 'Etherscan', url: 'https://etherscan.io' },
  },
});

export const acrossArbitrum = defineChain({
  id: 42161,
  name: 'Arbitrum One',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://arb1.arbitrum.io/rpc'] },
  },
  blockExplorers: {
    default: { name: 'Arbiscan', url: 'https://arbiscan.io' },
  },
});

export const acrossBase = defineChain({
  id: 8453,
  name: 'Base',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://mainnet.base.org'] },
  },
  blockExplorers: {
    default: { name: 'Basescan', url: 'https://basescan.org' },
  },
});

export const acrossInk = defineChain({
  id: ACROSS_INK_CHAIN_ID,
  name: 'Ink',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc-gel.inkonchain.com'] },
  },
  blockExplorers: {
    default: { name: 'Ink Explorer', url: 'https://explorer.inkonchain.com' },
  },
});

/** Native USDC address on Ink (Circle FiatTokenV2) */
export const INK_NATIVE_USDC = '0x2D270e6886d130D724215A266106e6832161EAEd' as const;

/** Native USDT0 address on Ink */
export const INK_USDT0 = '0x0200C29006150606B650577BBE7B6248F58470c1' as const;

export const ACROSS_INK_USDT0_TOKEN: AcrossTokenConfig = {
  chainId: ACROSS_INK_CHAIN_ID,
  symbol: 'USDT0',
  address: INK_USDT0,
  decimals: 6,
  bridgeType: 'none',
  productId: 0,
};

export const ACROSS_INK_USDC_TOKEN: AcrossTokenConfig = {
  chainId: ACROSS_INK_CHAIN_ID,
  symbol: 'USDC',
  address: INK_NATIVE_USDC,
  decimals: 6,
  bridgeType: 'none',
  productId: 5,
};

/** Across SpokePool contract on Ink — approve USDC here for fast withdrawals */
export const INK_ACROSS_SPOKE_POOL = '0xeF684C38F94F48775959ECf2012D7E864ffb9dd4' as const;

/** Token addresses per chain for Across route construction. */
export const ACROSS_TOKEN_BY_CHAIN: Record<
  number,
  Partial<Record<'USDT' | 'USDT0' | 'USDC', `0x${string}`>>
> = {
  1: {
    USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
    USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  },
  42161: {
    USDT: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
    USDT0: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9',
    USDC: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  },
  8453: {
    USDT: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
    USDC: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  },
  57073: {
    USDT0: INK_USDT0,
    USDC: INK_NATIVE_USDC,
  },
};

/** Chains supported by our Across integration — with reliable RPCs */
export const ACROSS_CHAINS = [acrossEthereum, acrossArbitrum, acrossBase, acrossInk] as const;
export const ACROSS_EVM_CHAINS = ACROSS_CHAINS;

const ACROSS_DISPLAY_CHAINS: AcrossDisplayChain[] = [
  {
    chainId: 1,
    name: 'Ethereum',
    nativeSymbol: 'ETH',
    blockExplorerUrl: 'https://etherscan.io',
    isPrimary: false,
  },
  {
    chainId: 42161,
    name: 'Arbitrum One',
    nativeSymbol: 'ETH',
    blockExplorerUrl: 'https://arbiscan.io',
    isPrimary: false,
  },
  {
    chainId: 8453,
    name: 'Base',
    nativeSymbol: 'ETH',
    blockExplorerUrl: 'https://basescan.org',
    isPrimary: false,
  },
  {
    chainId: ACROSS_INK_CHAIN_ID,
    name: 'Ink',
    nativeSymbol: 'ETH',
    blockExplorerUrl: 'https://explorer.inkonchain.com',
    isPrimary: true,
  },
];

export const ACROSS_DISPLAY_CHAIN_BY_ID: Record<number, AcrossDisplayChain> =
  Object.fromEntries(ACROSS_DISPLAY_CHAINS.map((chain) => [chain.chainId, chain]));

function makeAcrossToken(
  chainId: number,
  symbol: 'USDC' | 'USDT',
  address: `0x${string}`,
): AcrossTokenConfig {
  const isUsdc = symbol === 'USDC';

  return {
    chainId,
    symbol,
    address,
    decimals: 6,
    bridgeType: 'across',
    destinationToken: isUsdc ? INK_NATIVE_USDC : INK_USDT0,
    productId: isUsdc ? 5 : 0,
  };
}

export const ACROSS_INPUT_TOKENS_BY_CHAIN_ID: Record<number, AcrossTokenConfig[]> = {
  1: [
    makeAcrossToken(1, 'USDT', ACROSS_TOKEN_BY_CHAIN[1].USDT!),
    makeAcrossToken(1, 'USDC', ACROSS_TOKEN_BY_CHAIN[1].USDC!),
  ],
  42161: [
    makeAcrossToken(42161, 'USDT', ACROSS_TOKEN_BY_CHAIN[42161].USDT!),
    makeAcrossToken(42161, 'USDC', ACROSS_TOKEN_BY_CHAIN[42161].USDC!),
  ],
  8453: [
    makeAcrossToken(8453, 'USDT', ACROSS_TOKEN_BY_CHAIN[8453].USDT!),
    makeAcrossToken(8453, 'USDC', ACROSS_TOKEN_BY_CHAIN[8453].USDC!),
  ],
  [ACROSS_INK_CHAIN_ID]: [ACROSS_INK_USDT0_TOKEN, ACROSS_INK_USDC_TOKEN],
};

export const ACROSS_WITHDRAW_TOKENS_BY_CHAIN_ID: Record<number, AcrossTokenConfig[]> = {
  ...ACROSS_INPUT_TOKENS_BY_CHAIN_ID,
};

export const USDC_BY_CHAIN: Record<number, `0x${string}`> =
  Object.fromEntries(
    Object.entries(ACROSS_INPUT_TOKENS_BY_CHAIN_ID)
      .map(([chainId, tokens]) => [
        Number(chainId),
        tokens.find((token) => token.symbol === 'USDC')?.address,
      ])
      .filter((entry): entry is [number, `0x${string}`] => !!entry[1]),
  );

export function getAcrossInputToken(
  chainId: number,
  symbol: string,
): AcrossTokenConfig | undefined {
  return ACROSS_INPUT_TOKENS_BY_CHAIN_ID[chainId]?.find(
    (token) => token.symbol === symbol && token.bridgeType === 'across',
  );
}

export function getAcrossInputTokenByAddress(
  chainId: number,
  address: string,
): AcrossTokenConfig | undefined {
  return ACROSS_INPUT_TOKENS_BY_CHAIN_ID[chainId]?.find(
    (token) =>
      token.address.toLowerCase() === address.toLowerCase() &&
      token.bridgeType === 'across',
  );
}

export function getAcrossWithdrawalToken(
  chainId: number,
  symbol: string,
): AcrossTokenConfig | undefined {
  return ACROSS_WITHDRAW_TOKENS_BY_CHAIN_ID[chainId]?.find(
    (token) => token.symbol === symbol && token.bridgeType === 'across',
  );
}

export function getAcrossSupportedDepositChains(): AcrossDisplayChain[] {
  return ACROSS_DISPLAY_CHAINS.filter(
    (chain) => !chain.isPrimary && !!ACROSS_INPUT_TOKENS_BY_CHAIN_ID[chain.chainId]?.length,
  );
}

export function getAcrossSupportedWithdrawalChains(
  symbol: 'USDC' | 'USDT' | 'USDT0',
): AcrossDisplayChain[] {
  return ACROSS_DISPLAY_CHAINS.filter((chain) => {
    if (chain.isPrimary) return false;
    return !!getAcrossWithdrawalToken(chain.chainId, symbol);
  });
}

export function isAllowedAcrossDepositRoute(
  originChainId: number,
  inputToken: string,
  outputToken?: string,
): boolean {
  const token = getAcrossInputTokenByAddress(originChainId, inputToken);
  if (!token) return false;
  if (!outputToken) return true;
  return token.destinationToken?.toLowerCase() === outputToken.toLowerCase();
}

export function isAllowedAcrossWithdrawalRoute(
  destinationChainId: number,
  outputToken: string,
): boolean {
  return !!ACROSS_WITHDRAW_TOKENS_BY_CHAIN_ID[destinationChainId]?.some(
    (token) =>
      token.address.toLowerCase() === outputToken.toLowerCase() &&
      token.bridgeType === 'across',
  );
}

export function isLegacyEthereumUsdtAcrossInput({
  originChainId,
  inputToken,
}: {
  originChainId: number;
  inputToken: string;
}): boolean {
  return (
    originChainId === 1 &&
    inputToken.toLowerCase() === ACROSS_TOKEN_BY_CHAIN[1].USDT?.toLowerCase()
  );
}

export function normalizeAcrossSpenderAddress(
  spender: `0x${string}`,
): `0x${string}` {
  if (spender.length === 42) {
    return spender;
  }

  if (spender.length === 66 && /^0{24}$/i.test(spender.slice(2, 26))) {
    return `0x${spender.slice(-40)}`;
  }

  throw new Error(`Unsupported Across spender address: ${spender}`);
}
